<template>
    <md-list-item
        class="ql-project-bookmark-item-wrapper"
        :class="{ 'ql-project-bookmark-current': Number(page) === Number(currentPage) }">
        <div class="ql-project-bookmark-item" v-on:click="gotoPage" v-on:dblclick="edit">
            <span class="bookmarkLabel" v-if="versionID">
                <md-icon class="bookmarkIcon">bookmark_border</md-icon>
                p. {{currentVersion.pdfPageNumbers && currentVersion.pdfPageNumbers[page - 1] && currentVersion.pdfPageNumbers[page - 1].name}}
            </span>
            <span class="bookmarkLabel" v-else>
                <md-icon class="bookmarkIcon">bookmark_border</md-icon>
                p. {{project.pdfPageNumbers && project.pdfPageNumbers[page - 1] && project.pdfPageNumbers[page - 1].name}}
            </span>
            <div class="bookmarkTitle" v-if="!editing">{{title}}</div>
            <form novalidate @submit.stop.prevent="saveTitle" @keyup.stop.prevent="keyPress" v-if="editing">
                <md-input-container>
                    <md-input v-model="oldTitle" v-on:blur="done" @keyup.stop.prevent="keyPress" ref="input_title"></md-input>
                </md-input-container>
            </form>
            <md-tooltip>Double Click to Edit</md-tooltip>
        </div>
    </md-list-item>
</template>

<script>
import { mapGetters } from 'vuex';
import { bookmarkRename } from '@/lib/bookmark-rename';

const BookmarkItem = {
    name: 'BookmarkItem',
    props: {
        page: {},
        title: {},
        currentPage: {},
        // Set when this bookmark was just created, so it opens straight into
        // title editing instead of requiring a double-click.
        startEdit: { type: Boolean, default: false }
    },
    data: function() {
        return {
            editing: false,
            oldTitle: this.title
        };
    },
    watch: {
        title() {
            this.oldTitle = this.title;
        },
        startEdit: {
            immediate: true,
            handler(shouldEdit) {
                if (!shouldEdit || this.editing) {
                    return;
                }
                // Tell the parent to drop the request before opening the field,
                // so re-renders (page change, another bookmark added) don't
                // re-trigger editing on this row.
                this.$emit('editStarted');
                this.$nextTick(() => this.edit(true));
            }
        }
    },
    computed: {
        ...mapGetters([
            'project',
            'currentVersion'
        ]),
        versionID: function() {
            return this.currentVersion ? this.currentVersion.id : '';
        }
    },
    methods: {
        edit(selectAll = false) {
            this.editing = true;
            this.$nextTick(() => {
                setTimeout(() => {
                    const input = this.$refs.input_title;
                    if (!input || !input.$el) {
                        return;
                    }
                    input.$el.focus();
                    // A freshly added bookmark is pre-filled with the page name;
                    // select it so typing replaces it instead of appending.
                    if (selectAll && input.$el.select) {
                        input.$el.select();
                    }
                }, 200);
            });
        },
        done() {
            this.editing = false;
            this.$store.dispatch(...bookmarkRename(this.versionID, this.page, this.oldTitle));
        },
        keyPress(e) {
            if (e.keyCode === 27) {
                this.editing = false;
                this.oldTitle = this.title;
            }
        },
        saveTitle() {
            this.editing = false;
            this.$store.dispatch(...bookmarkRename(this.versionID, this.page, this.oldTitle));
        },
        gotoPage() {
            this.$emit('gotoPage', Number(this.page));
        }
    }
};
export default BookmarkItem;

</script>

<style lang="scss">
/*
 * Rows here deliberately mirror .ql-marker-nav-item (Structure tab) so the two
 * tabs of the Navigation panel read as one component: same 32px row height,
 * same 6px/16px/12px padding, same 18px icon, same weight and colour.
 */
.bookmarks-list {
    max-height: calc(100vh - 128px);
    overflow: auto;
    padding: 0px 0;
    .ql-project-bookmark-item-wrapper {
        cursor: pointer;
        min-height: 32px;
        font-weight: 400;
        transition: background 0.25s ease;
        &:hover {
            background: #eee;
        }
        &.ql-project-bookmark-current {
            background: #f5f5f5;
        }
        .md-list-item-container {
            max-width: 100%;
            overflow: hidden;
            padding: 6px 16px 6px 12px;
            font-weight: 400;
        }
    }
    &.md-theme-show-mode {
        .ql-project-bookmark-item-wrapper:hover {
            background: #333;
        }
    }
}
.md-theme-show-mode .ql-project-bookmark-item-wrapper {
    &.ql-project-bookmark-current {
        background: rgba(255, 255, 255, 0.08);
    }

    .bookmarkIcon {
        color: #fff !important;
    }

    .bookmarkLabel,
    .bookmarkTitle {
        color: rgba(255, 255, 255, 0.9);
    }
}
.ql-project-bookmark-item {
    display: flex;
    flex: 1;
    align-items: center;
    width: 100%;
    /* Matches .ql-marker-nav-text in the Structure tab. Without this the rows
       inherited md-list's 16px while Structure rows were 13px, which is what
       made the two tabs look like different components. */
    font-size: 13px;
    line-height: 1.35;
    .md-input-container {
        margin: 0;
        padding: 0;
        min-height: 0;
    }
    /* The inline title editor sits in the same row — keep it on the same type
       so the row doesn't jump size when you double-click to rename. */
    .md-input {
        font-size: 13px;
        line-height: 1.35;
    }
}
.bookmarkIcon {
    flex: 0 0 18px;
    width: 18px;
    height: 18px;
    min-width: 18px;
    font-size: 18px !important;
    margin-right: 10px;
    color: #222 !important;
}
.bookmarkLabel {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    margin-right: 6px;
    font-weight: 400;
    color: #222;
}
.bookmarkTitle {
    flex: 1 1 auto;
    min-width: 0;
    font-weight: 400;
    color: #222;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.md-theme-show-mode {
    .bookmarkLabel,
    .bookmarkTitle {
        color: rgba(255, 255, 255, 0.9);
    }
}
//cue issue
</style>


// WEBPACK FOOTER //
// bookmark.vue?c731ab02